import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// PrimeNG v20 imports - mixing modules and standalone components
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaginatorModule } from 'primeng/paginator';
import { ToolbarModule } from 'primeng/toolbar';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ChartModule } from 'primeng/chart';
import { AccordionModule } from 'primeng/accordion';
import { TextareaModule } from 'primeng/textarea';
import { TreeSelectModule } from 'primeng/treeselect';
import { StepsModule } from 'primeng/steps';
import { TabsModule } from 'primeng/tabs';

import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';

// Services
import { CustomReportService } from '../../core/services/custom-report.service';

// Field Definitions
import { COMPREHENSIVE_FIELD_DEFINITIONS, CALCULATED_FIELD_TEMPLATES, FIELD_CATEGORIES } from './field-definitions';

// Report Templates
import { REPORT_TEMPLATES, TEMPLATE_CATEGORIES, ReportTemplate } from './report-templates';

interface ReportField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'array';
  source: string;
  path: string;
  category: string;
  description?: string;
  accessLevel: string[];
  isCalculated?: boolean;
  formula?: string;
  dependsOn?: string[];
}

interface CalculatedField {
  name: string;
  label: string;
  formula: string;
  type: 'aggregation' | 'mathematical' | 'conditional' | 'date' | 'window';
  returnType: 'string' | 'number' | 'date' | 'boolean';
  description?: string;
  parameters?: { [key: string]: any };
  expanded?: boolean;
}

interface GroupByConfig {
  fields: string[];
  aggregations: AggregationConfig[];
  having?: QueryCondition[];
}

interface AggregationConfig {
  field: string;
  function: 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX' | 'COUNT_DISTINCT';
  alias: string;
  filter?: QueryCondition;
}

interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
  nullsLast?: boolean;
}

interface ChartConfig {
  enabled: boolean;
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'radar' | 'scatter' | 'bubble';
  xAxis: string;
  yAxis: string[];
  title?: string;
  description?: string;
  colors?: string[];
  options?: any;
}

interface QueryCondition {
  field: string;
  operator: string;
  value: any;
  secondValue?: any;
}

interface CustomReport {
  _id?: string;
  name: string;
  description?: string;
  selectedFields: string[];
  calculatedFields?: CalculatedField[];
  conditions: QueryCondition[];
  groupBy?: GroupByConfig;
  orderBy?: SortConfig[];
  limit?: number;
  isPublic: boolean;
  exportFormats: string[];
  chartConfig?: ChartConfig;
  createdBy?: any;
  subscriberId?: string;
  lastExecuted?: Date;
  executionCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

interface ExecutionResult {
  data: any[];
  total: number;
  executionTime: number;
  aggregatedData?: any;
  chartData?: any;
  metadata?: {
    groupedBy?: string[];
    aggregations?: string[];
    hasCalculatedFields?: boolean;
  };
}

@Component({
  selector: 'app-custom-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    InputNumberModule,
    CheckboxModule,
    CardModule,
    PanelModule,
    DrawerModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    ProgressSpinnerModule,
    PaginatorModule,
    ToolbarModule,
    SplitButtonModule,
    MenuModule,
    TagModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    ChartModule,
    AccordionModule,
    TextareaModule,
    TreeSelectModule,
    StepsModule,
    TabsModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './custom-reports.html',
  styleUrl: './custom-reports.scss'
})
export class CustomReports implements OnInit {
  // Data properties
  reports: CustomReport[] = [];
  availableFields: ReportField[] = [];
  fieldsByCategory: { [category: string]: ReportField[] } = {};
  
  // UI state
  loading = false;
  reportBuilderVisible = false;
  reportPreviewVisible = false;
  reportExecutionVisible = false;
  isEditMode = false;
  chartViewVisible = false;
  builderStep = 0;
  templatesVisible = false;
  
  // New feature states
  calculatedFieldsVisible = false;
  groupByVisible = false;
  chartConfigVisible = false;
  
  // Template data
  reportTemplates: ReportTemplate[] = REPORT_TEMPLATES;
  templateCategories = Object.keys(TEMPLATE_CATEGORIES);
  
  // Current report being built/edited
  currentReport: CustomReport | null = null;
  reportForm!: FormGroup;
  
  // Query builder
  selectedFields: ReportField[] = [];
  conditions: QueryCondition[] = [];
  calculatedFields: CalculatedField[] = [];
  groupByConfig: GroupByConfig | null = null;
  sortConfig: SortConfig[] = [];
  chartConfig: ChartConfig | null = null;
  
  // Preview and execution
  previewData: any[] = [];
  executionResult: ExecutionResult | null = null;
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  chartData: any = null;
  chartOptions: any = {};
  
  // Backend validation feedback
  validationFeedbackVisible = false;
  validationFeedback: { message?: string; errors: string[]; warnings: string[]; context?: string } = {
    message: '',
    errors: [],
    warnings: [],
    context: ''
  };
  
  // Field selection
  fieldSearchText = '';
  selectedCategory = '';
  
  // Available options
  operatorOptions = [
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'not_equals' },
    { label: 'Contains', value: 'contains' },
    { label: 'Not Contains', value: 'not_contains' },
    { label: 'Starts With', value: 'starts_with' },
    { label: 'Ends With', value: 'ends_with' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Greater Than or Equal', value: 'greater_than_equal' },
    { label: 'Less Than or Equal', value: 'less_than_equal' },
    { label: 'Between', value: 'between' },
    { label: 'In List', value: 'in' },
    { label: 'Not In List', value: 'not_in' },
    { label: 'Is Null', value: 'is_null' },
    { label: 'Is Not Null', value: 'is_not_null' },
    { label: 'Date Range', value: 'date_range' }
  ];
  
  aggregationOptions = [
    { label: 'Sum', value: 'SUM' },
    { label: 'Average', value: 'AVG' },
    { label: 'Count', value: 'COUNT' },
    { label: 'Count Distinct', value: 'COUNT_DISTINCT' },
    { label: 'Minimum', value: 'MIN' },
    { label: 'Maximum', value: 'MAX' }
  ];
  
  chartTypeOptions = [
    { label: 'Bar Chart', value: 'bar', icon: 'pi pi-chart-bar' },
    { label: 'Line Chart', value: 'line', icon: 'pi pi-chart-line' },
    { label: 'Pie Chart', value: 'pie', icon: 'pi pi-chart-pie' },
    { label: 'Doughnut Chart', value: 'doughnut', icon: 'pi pi-circle' },
    { label: 'Radar Chart', value: 'radar', icon: 'pi pi-sync' },
    { label: 'Scatter Plot', value: 'scatter', icon: 'pi pi-ellipsis-h' }
  ];
  
  calculationTypeOptions = [
    { label: 'Mathematical', value: 'mathematical', description: 'Basic math operations (+, -, *, /)' },
    { label: 'Aggregation', value: 'aggregation', description: 'Sum, average, count, etc.' },
    { label: 'Conditional', value: 'conditional', description: 'IF-THEN-ELSE logic' },
    { label: 'Date/Time', value: 'date', description: 'Date calculations and formatting' },
    { label: 'Window Function', value: 'window', description: 'Running totals, ranks, etc.' }
  ];
  
  // Builder steps for wizard
  builderSteps = [
    { label: 'Fields', icon: 'pi pi-list' },
    { label: 'Calculations', icon: 'pi pi-calculator' },
    { label: 'Grouping', icon: 'pi pi-objects-column' },
    { label: 'Filters', icon: 'pi pi-filter' },
    { label: 'Charts', icon: 'pi pi-chart-bar' },
    { label: 'Settings', icon: 'pi pi-cog' }
  ];
  
  exportOptions = [
    { label: 'CSV', value: 'csv' },
    { label: 'Excel', value: 'xls' },
    { label: 'PDF', value: 'pdf' }
  ];

  // Action menus
  reportActionItems: MenuItem[] = [];
  exportActionItems: MenuItem[] = [];

  constructor(
    private customReportService: CustomReportService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {
    this.initializeForm();
    this.initializeActionMenus();
  }

  ngOnInit() {
    this.loadReports();
    this.loadAvailableFields();
  }

  private initializeForm() {
    this.reportForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(500)],
      isPublic: [false],
      limit: [1000, [Validators.min(1), Validators.max(10000)]],
      exportFormats: [['csv']],
      enableGrouping: [false],
      enableCharting: [false]
    });
  }

  private initializeActionMenus() {
    this.reportActionItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editReport(this.currentReport!)
      },
      {
        label: 'Duplicate',
        icon: 'pi pi-copy',
        command: () => this.duplicateReport(this.currentReport!)
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        command: () => this.deleteReport(this.currentReport!)
      }
    ];

    this.exportActionItems = [
      {
        label: 'Export CSV',
        icon: 'pi pi-file',
        command: () => this.exportReport('csv')
      },
      {
        label: 'Export Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportReport('excel')
      },
      {
        label: 'Export PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportReport('pdf')
      }
    ];
  }

  // Data loading methods
  async loadReports() {
    try {
      this.loading = true;
      this.reports = await this.customReportService.getUserReports();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load reports'
      });
    } finally {
      this.loading = false;
    }
  }

  async loadAvailableFields() {
    try {
      // Use comprehensive field definitions
      this.availableFields = COMPREHENSIVE_FIELD_DEFINITIONS;
      
      // Group fields by category
      this.fieldsByCategory = {};
      FIELD_CATEGORIES.forEach(category => {
        this.fieldsByCategory[category] = this.availableFields.filter(f => f.category === category);
      });
      
      // Also try to load from service for any additional fields
      try {
        const response = await this.customReportService.getAvailableFields();
        if (response.fields && response.fields.length > 0) {
          // Merge with existing fields, avoiding duplicates
          response.fields.forEach(field => {
            if (!this.availableFields.find(f => f.name === field.name)) {
              this.availableFields.push(field);
              if (!this.fieldsByCategory[field.category]) {
                this.fieldsByCategory[field.category] = [];
              }
              this.fieldsByCategory[field.category].push(field);
            }
          });
        }
      } catch (serviceError) {
        console.warn('Service fields not available, using local definitions only');
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load available fields'
      });
    }
  }

  // Report builder methods
  createNewReport() {
    this.isEditMode = false;
    this.currentReport = null;
    this.resetForm();
    this.resetBuilder();
    this.builderStep = 0;
    this.reportBuilderVisible = true;
  }
  
  private resetBuilder() {
    this.selectedFields = [];
    this.conditions = [];
    this.calculatedFields = [];
    this.groupByConfig = null;
    this.sortConfig = [];
    this.chartConfig = null;
    this.chartData = null;
  }

  editReport(report: CustomReport) {
    this.isEditMode = true;
    this.currentReport = report;
    this.populateForm(report);
    this.populateSelectedFields(report.selectedFields);
    this.conditions = [...report.conditions];
    this.calculatedFields = [...(report.calculatedFields || [])];
    this.groupByConfig = report.groupBy ? { ...report.groupBy } : null;
    this.sortConfig = [...(report.orderBy || [])];
    this.chartConfig = report.chartConfig ? { ...report.chartConfig } : null;
    this.builderStep = 0;
    this.reportBuilderVisible = true;
  }

  duplicateReport(report: CustomReport) {
    this.isEditMode = false;
    this.currentReport = null;
    this.populateForm({
      ...report,
      name: `${report.name} (Copy)`,
      _id: undefined
    });
    this.populateSelectedFields(report.selectedFields);
    this.conditions = [...report.conditions];
    this.reportBuilderVisible = true;
  }

  private resetForm() {
    this.reportForm.reset({
      name: '',
      description: '',
      isPublic: false,
      limit: 1000,
      exportFormats: ['csv']
    });
  }

  private populateForm(report: Partial<CustomReport>) {
    this.reportForm.patchValue({
      name: report.name,
      description: report.description,
      isPublic: report.isPublic,
      limit: report.limit,
      exportFormats: report.exportFormats
    });
  }

  private populateSelectedFields(fieldNames: string[]) {
    this.selectedFields = fieldNames.map(name => 
      this.availableFields.find(f => f.name === name)!
    ).filter(Boolean);
  }

  // Field selection methods
  get filteredFields(): ReportField[] {
    let fields = this.availableFields;
    
    if (this.selectedCategory) {
      fields = fields.filter(f => f.category === this.selectedCategory);
    }
    
    if (this.fieldSearchText) {
      const searchLower = this.fieldSearchText.toLowerCase();
      fields = fields.filter(f => 
        f.label.toLowerCase().includes(searchLower) ||
        f.description?.toLowerCase().includes(searchLower)
      );
    }
    
    return fields;
  }

  get categories(): string[] {
    return Object.keys(this.fieldsByCategory);
  }

  toggleFieldSelection(field: ReportField) {
    const index = this.selectedFields.findIndex(f => f.name === field.name);
    if (index > -1) {
      this.selectedFields.splice(index, 1);
    } else {
      this.selectedFields.push(field);
    }
  }

  isFieldSelected(field: ReportField): boolean {
    return this.selectedFields.some(f => f.name === field.name);
  }

  removeSelectedField(field: ReportField) {
    const index = this.selectedFields.findIndex(f => f.name === field.name);
    if (index > -1) {
      this.selectedFields.splice(index, 1);
    }
  }

  // Condition methods
  addCondition() {
    if (this.selectedFields.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select at least one field before adding conditions'
      });
      return;
    }
    
    this.conditions.push({
      field: this.selectedFields[0].name,
      operator: 'equals',
      value: ''
    });
  }

  removeCondition(index: number) {
    this.conditions.splice(index, 1);
  }

  getFieldForCondition(fieldName: string): ReportField | undefined {
    return this.availableFields.find(f => f.name === fieldName);
  }

  getOperatorsForField(field: ReportField): any[] {
    if (!field) return this.operatorOptions;
    
    const allOperators = [...this.operatorOptions];
    
    if (field.type === 'number') {
      return allOperators;
    } else if (field.type === 'date') {
      return allOperators.filter(op => 
        ['equals', 'not_equals', 'greater_than', 'less_than', 
         'greater_than_equal', 'less_than_equal', 'between', 'date_range',
         'is_null', 'is_not_null'].includes(op.value)
      );
    } else if (field.type === 'boolean') {
      return allOperators.filter(op => 
        ['equals', 'not_equals', 'is_null', 'is_not_null'].includes(op.value)
      );
    } else {
      return allOperators.filter(op => 
        !['greater_than', 'less_than', 'greater_than_equal', 'less_than_equal'].includes(op.value)
      );
    }
  }

  needsSecondValue(operator: string): boolean {
    return ['between', 'date_range'].includes(operator);
  }

  needsListValue(operator: string): boolean {
    return ['in', 'not_in'].includes(operator);
  }

  needsNoValue(operator: string): boolean {
    return ['is_null', 'is_not_null'].includes(operator);
  }

  // Preview and execution methods
  async previewReport() {
    if (!this.validateReport()) {
      return;
    }

    try {
      this.loading = true;
      const reportData = this.buildReportData();
      
      // Debug: Log the payload being sent
      console.log('Preview Report Payload:', JSON.stringify(reportData, null, 2));
      
      this.previewData = await this.customReportService.previewReport(reportData);
      
      // Debug logging for preview
      console.log('Preview Data:', this.previewData);
      console.log('Selected Fields:', this.selectedFields);
      console.log('Preview Data Columns:', this.getPreviewDataColumns());
      console.log('Has Matching Preview Fields:', this.hasMatchingPreviewFields());
      
      this.reportPreviewVisible = true;
    } catch (error: any) {
      console.error('Preview Error Details:', error);

      // Show structured backend validation feedback when available
      const payload = error?.error || {};
      if (payload && (Array.isArray(payload.errors) || Array.isArray(payload.warnings))) {
        this.showBackendErrors(payload, 'Preview Error');
      } else {
        const errorMessage = payload?.message || error?.message || 'Failed to preview report';
        this.showBackendErrors({ message: errorMessage, errors: [], warnings: [] }, 'Preview Error');
      }
    } finally {
      this.loading = false;
    }
  }

  async saveReport() {
    if (!this.validateReport()) {
      return;
    }

    try {
      this.loading = true;
      const reportData = this.buildReportData();
      
      if (this.isEditMode && this.currentReport) {
        await this.customReportService.updateReport(this.currentReport._id!, reportData);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Report updated successfully',
          life: 3000
        });
      } else {
        await this.customReportService.createReport(reportData);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Report created successfully',
          life: 3000
        });
      }
      
      this.reportBuilderVisible = false;
      this.loadReports();
    } catch (error: any) {
      const payload = error?.error || {};
      if (payload && (Array.isArray(payload.errors) || Array.isArray(payload.warnings))) {
        this.showBackendErrors(payload, 'Save Error');
      } else {
        this.showBackendErrors({ message: payload?.message || error.message || 'Failed to save report', errors: [], warnings: [] }, 'Save Error');
      }
    } finally {
      this.loading = false;
    }
  }

  async executeReport(report: CustomReport) {
    try {
      this.loading = true;
      this.currentReport = report;
      
      console.log('Executing report:', report.name, 'ID:', report._id);
      console.log('Report configuration:', {
        selectedFields: report.selectedFields,
        conditions: report.conditions,
        orderBy: report.orderBy
      });
      
      try {
        console.log('About to call executeReport service...');
        this.executionResult = await this.customReportService.executeReport(
          report._id!,
          this.currentPage,
          this.pageSize
        );
        console.log('Service call completed successfully');
      } catch (serviceError) {
        console.error('Service call failed:', serviceError);
        throw serviceError;
      }
      
      console.log('Raw execution result:', this.executionResult);
      console.log('Type of execution result:', typeof this.executionResult);
      console.log('Is execution result an object?', this.executionResult && typeof this.executionResult === 'object');
      
      if (!this.executionResult) {
        throw new Error('No execution result received');
      }
      
      console.log('Checking data property...');
      console.log('executionResult.data:', this.executionResult.data);
      console.log('Type of data:', typeof this.executionResult.data);
      console.log('Is data an array?', Array.isArray(this.executionResult.data));
      
      if (!this.executionResult.data || !Array.isArray(this.executionResult.data)) {
        console.warn('Invalid or missing data in execution result:', this.executionResult);
        this.executionResult.data = [];
      }
      
      console.log('Setting totalRecords...');
      this.totalRecords = this.executionResult.total || 0;
      console.log('totalRecords set to:', this.totalRecords);
      
      // Debug logging
      console.log('Processed Execution Result:', {
        dataLength: this.executionResult.data.length,
        total: this.totalRecords,
        executionTime: this.executionResult.executionTime,
        firstRow: this.executionResult.data[0]
      });
      
      // Auto-detect columns if selectedFields don't match data
      console.log('Starting field matching check...');
      console.log('Data length:', this.executionResult.data.length);
      console.log('Selected fields length:', report.selectedFields?.length || 0);
      
      try {
        if (this.executionResult.data.length > 0 && report.selectedFields && report.selectedFields.length > 0) {
          console.log('Getting data keys from first row...');
          const firstRow = this.executionResult.data[0];
          console.log('First row:', firstRow);
          
          if (!firstRow || typeof firstRow !== 'object') {
            console.warn('First row is not a valid object:', firstRow);
          } else {
            const dataKeys = Object.keys(firstRow);
            console.log('Data keys extracted:', dataKeys);
            
            const hasMatchingFields = report.selectedFields.some(field => dataKeys.includes(field));
            
            console.log('Field matching check:', {
              selectedFields: report.selectedFields,
              dataKeys: dataKeys,
              hasMatchingFields: hasMatchingFields
            });
            
            if (!hasMatchingFields) {
              console.warn('Selected fields do not match data keys. Auto-detecting columns...');
              // Temporarily override selectedFields with actual data keys
              this.currentReport = {
                ...report,
                selectedFields: dataKeys
              };
              console.log('Updated currentReport with auto-detected fields');
            }
          }
        } else {
          console.log('Skipping field matching - no data or no selected fields');
        }
      } catch (fieldMatchingError) {
        console.error('Error during field matching:', fieldMatchingError);
        // Don't throw here, just log and continue
      }
      
      // Generate chart data if chart is configured
      console.log('Checking chart configuration...');
      try {
        if (report.chartConfig && report.chartConfig.enabled) {
          console.log('Chart is enabled, generating chart data...');
          this.chartConfig = report.chartConfig;
          this.generateChartOptions();
          this.generateChartData();
          console.log('Chart data generated successfully');
        } else {
          console.log('Chart is not enabled or not configured');
        }
      } catch (chartError) {
        console.error('Error generating chart data:', chartError);
        // Don't throw here, just log and continue without chart
      }
      
      console.log('Setting reportExecutionVisible to true...');
      this.reportExecutionVisible = true;
      console.log('Execution completed successfully!');
    } catch (error: any) {
      console.error('Error executing report:', error);
      console.error('Error stack:', error?.stack);

      const payload = error?.error || {};
      if (payload && (Array.isArray(payload.errors) || Array.isArray(payload.warnings))) {
        this.showBackendErrors(payload, 'Execution Error');
      } else {
        let errorMessage = payload?.message || error?.message || 'Failed to execute report';
        if (typeof errorMessage === 'string' && errorMessage.includes("Cannot read properties of undefined (reading 'length')")) {
          errorMessage = 'Data structure error during execution. Please check the console for details.';
          console.error('Length error details:', {
            executionResult: this.executionResult,
            currentReport: this.currentReport,
            selectedFields: this.selectedFields,
            calculatedFields: this.calculatedFields,
            previewData: this.previewData
          });
        }
        this.showBackendErrors({ message: errorMessage, errors: [], warnings: [] }, 'Execution Error');
      }
    } finally {
      this.loading = false;
    }
  }

  // Display backend validation errors and warnings in a dismissible card
  private showBackendErrors(payload: any, context: string = 'Validation Error') {
    this.validationFeedback.message = payload?.message || 'Invalid report configuration';
    this.validationFeedback.errors = Array.isArray(payload?.errors) ? payload.errors : [];
    this.validationFeedback.warnings = Array.isArray(payload?.warnings) ? payload.warnings : [];
    this.validationFeedback.context = context;
    this.validationFeedbackVisible = true;
  }

  dismissValidationFeedback() {
    this.validationFeedbackVisible = false;
    this.validationFeedback = { message: '', errors: [], warnings: [], context: '' };
  }

  async onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    if (this.currentReport) {
      await this.executeReport(this.currentReport);
    }
  }

  // Calculated Fields Methods
  addCalculatedField() {
    const newField: CalculatedField = {
      name: `calc_field_${this.calculatedFields.length + 1}`,
      label: '',
      formula: '',
      type: 'mathematical',
      returnType: 'number',
      description: '',
      parameters: {},
      expanded: true
    };
    this.calculatedFields.push(newField);
  }

  addCalculatedFieldFromTemplate(template: any) {
    const newField: CalculatedField = {
      name: template.name,
      label: template.label,
      formula: template.formula,
      type: template.type,
      returnType: template.returnType,
      description: template.description,
      parameters: {},
      expanded: true
    };
    this.calculatedFields.push(newField);
  }

  getCalculatedFieldTemplates(): any[] {
    return CALCULATED_FIELD_TEMPLATES;
  }

  // Template Methods
  showTemplates() {
    this.templatesVisible = true;
  }

  applyReportTemplate(template: ReportTemplate) {
    this.isEditMode = false;
    this.currentReport = null;
    
    // Apply template settings
    this.reportForm.patchValue({
      name: template.name,
      description: template.description,
      isPublic: template.isPublic,
      exportFormats: template.exportFormats
    });
    
    // Apply selected fields
    this.selectedFields = template.selectedFields.map(fieldName => 
      this.availableFields.find(f => f.name === fieldName)!
    ).filter(Boolean);
    
    // Apply calculated fields
    this.calculatedFields = template.calculatedFields.map(calc => ({
      ...calc,
      expanded: false
    }));
    
    // Apply conditions
    this.conditions = [...template.conditions];
    
    // Apply grouping
    this.groupByConfig = template.groupBy ? { ...template.groupBy } : null;
    if (this.groupByConfig) {
      this.reportForm.patchValue({ enableGrouping: true });
    }
    
    // Apply sorting
    this.sortConfig = template.orderBy ? [...template.orderBy] : [];
    
    // Apply chart configuration
    this.chartConfig = template.chartConfig ? { ...template.chartConfig } : null;
    if (this.chartConfig) {
      this.reportForm.patchValue({ enableCharting: true });
      // Fix chart configuration to ensure fields exist
      setTimeout(() => {
        this.fixChartConfiguration();
      }, 100);
    }
    
    // Close templates and open builder
    this.templatesVisible = false;
    this.builderStep = 0;
    this.reportBuilderVisible = true;
    
    this.messageService.add({
      severity: 'success',
      summary: 'Template Applied',
      detail: `Template "${template.name}" has been applied successfully`,
      life: 3000
    });
  }

  getTemplatesByCategory(category: string): ReportTemplate[] {
    return this.reportTemplates.filter(t => t.category === category);
  }

  removeCalculatedField(index: number) {
    this.calculatedFields.splice(index, 1);
  }

  validateFormula(formula: string): boolean {
    // Basic formula validation - can be enhanced
    if (!formula.trim()) return false;
    
    // Check for basic syntax
    const allowedChars = /^[a-zA-Z0-9_\+\-\*\/\(\)\s\.,]+$/;
    return allowedChars.test(formula);
  }

  getAvailableFieldsForFormula(): ReportField[] {
    return this.selectedFields.filter(f => f.type === 'number' || f.type === 'date');
  }

  // Group By Methods
  initializeGroupBy() {
    if (!this.groupByConfig) {
      this.groupByConfig = {
        fields: [],
        aggregations: [],
        having: []
      };
    }
  }

  addGroupByField(fieldName: string) {
    this.initializeGroupBy();
    if (!this.groupByConfig!.fields.includes(fieldName)) {
      this.groupByConfig!.fields.push(fieldName);
    }
  }

  removeGroupByField(fieldName: string) {
    if (this.groupByConfig) {
      const index = this.groupByConfig.fields.indexOf(fieldName);
      if (index > -1) {
        this.groupByConfig.fields.splice(index, 1);
      }
    }
  }

  addAggregation() {
    this.initializeGroupBy();
    const newAggregation: AggregationConfig = {
      field: this.selectedFields[0]?.name || '',
      function: 'COUNT',
      alias: `agg_${this.groupByConfig!.aggregations.length + 1}`
    };
    this.groupByConfig!.aggregations.push(newAggregation);
  }

  removeAggregation(index: number) {
    if (this.groupByConfig) {
      this.groupByConfig.aggregations.splice(index, 1);
    }
  }

  // Chart Configuration Methods
  initializeChart() {
    if (!this.chartConfig) {
      this.chartConfig = {
        enabled: true,
        type: 'bar',
        xAxis: '',
        yAxis: [],
        title: this.reportForm.get('name')?.value || 'Chart',
        colors: ['#42A5F5', '#66BB6A', '#FFA726', '#EF5350', '#AB47BC', '#26C6DA'],
        options: {}
      };
    }
  }

  updateChartType(type: string) {
    this.initializeChart();
    this.chartConfig!.type = type as any;
    this.generateChartOptions();
  }

  generateChartOptions() {
    if (!this.chartConfig) return;

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: !!this.chartConfig.title,
          text: this.chartConfig.title
        },
        legend: {
          display: true,
          position: 'top'
        }
      }
    };

    switch (this.chartConfig.type) {
      case 'pie':
      case 'doughnut':
        this.chartConfig.options = {
          ...baseOptions,
          plugins: {
            ...baseOptions.plugins,
            legend: {
              display: true,
              position: 'right'
            }
          }
        };
        break;
      case 'line':
        this.chartConfig.options = {
          ...baseOptions,
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                display: true
              }
            },
            x: {
              grid: {
                display: false
              }
            }
          }
        };
        break;
      default:
        this.chartConfig.options = {
          ...baseOptions,
          scales: {
            y: {
              beginAtZero: true
            }
          }
        };
    }
  }

  generateChartData() {
    console.log('generateChartData called');
    
    if (!this.executionResult || !this.chartConfig) {
      console.log('Missing executionResult or chartConfig');
      return;
    }

    const data = this.executionResult.data;
    const xField = this.chartConfig.xAxis;
    const yFields = this.chartConfig.yAxis;

    console.log('Chart data generation inputs:', {
      dataLength: data?.length || 0,
      xField,
      yFields,
      yFieldsType: typeof yFields,
      yFieldsIsArray: Array.isArray(yFields)
    });

    if (!xField || !yFields || !Array.isArray(yFields) || yFields.length === 0) {
      console.log('Invalid chart configuration - missing or invalid fields');
      return;
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log('No data available for chart generation');
      return;
    }

    try {
      const labels = [...new Set(data.map(row => row && row[xField] ? row[xField] : 'Unknown'))];
      console.log('Chart labels:', labels);
      
      const datasets = yFields.map((yField, index) => {
        const fieldData = labels.map(label => {
          const rows = data.filter(row => row && row[xField] === label);
          return rows.reduce((sum, row) => sum + (parseFloat(row[yField]) || 0), 0);
        });

        const colors = this.chartConfig?.colors || ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];
        
        return {
          label: this.getFieldLabel(yField),
          data: fieldData,
          backgroundColor: colors[index % colors.length],
          borderColor: colors[index % colors.length],
          borderWidth: this.chartConfig?.type === 'line' ? 2 : 1,
          fill: this.chartConfig?.type === 'line' ? false : true
        };
      });

      this.chartData = {
        labels,
        datasets
      };
      
      console.log('Chart data generated successfully:', this.chartData);
    } catch (chartDataError) {
      console.error('Error generating chart data:', chartDataError);
      throw chartDataError;
    }
  }

  // Builder Navigation
  nextStep() {
    if (this.builderStep < this.builderSteps.length - 1) {
      this.builderStep++;
    }
  }

  prevStep() {
    if (this.builderStep > 0) {
      this.builderStep--;
    }
  }

  goToStep(step: number) {
    if (step >= 0 && step < this.builderSteps.length) {
      this.builderStep = step;
    }
  }

  // Sort Configuration
  addSortField() {
    if (this.selectedFields.length > 0) {
      const newSort: SortConfig = {
        field: this.selectedFields[0].name,
        direction: 'asc',
        nullsLast: true
      };
      this.sortConfig.push(newSort);
    }
  }

  removeSortField(index: number) {
    this.sortConfig.splice(index, 1);
  }

  // Export methods
  async exportReport(format: 'csv' | 'excel' | 'pdf') {
    if (!this.currentReport) return;

    try {
      this.loading = true;
      await this.customReportService.exportReport(this.currentReport._id!, format);
      this.messageService.add({
        severity: 'success',
        summary: 'Export Started',
        detail: `Report export to ${format.toUpperCase()} has started`,
        life: 3000
      });
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Export Error',
        detail: error.message || 'Failed to export report'
      });
    } finally {
      this.loading = false;
    }
  }

  // Validation and helper methods
  private validateReport(): boolean {
    if (this.reportForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields'
      });
      return false;
    }

    if (this.selectedFields.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select at least one field'
      });
      return false;
    }

    return true;
  }

  private buildReportData(): Partial<CustomReport> {
    const formValue = this.reportForm.value;
    
    // Clean and validate the payload
    const payload: any = {
      name: formValue.name,
      description: formValue.description,
      isPublic: formValue.isPublic || false,
      limit: formValue.limit || 1000,
      exportFormats: formValue.exportFormats || ['csv'],
      selectedFields: this.selectedFields.map(f => f.name),
      conditions: this.conditions.filter(c => 
        (c.value !== '' && c.value !== null && c.value !== undefined) || 
        this.needsNoValue(c.operator)
      )
    };
    
    // Add calculated fields if they exist and are valid
    if (this.calculatedFields && this.calculatedFields.length > 0) {
      payload.calculatedFields = this.calculatedFields.map(calc => ({
        name: calc.name,
        label: calc.label,
        formula: calc.formula,
        type: calc.type,
        returnType: calc.returnType,
        description: calc.description
      }));
    }
    
    // Add grouping only if enabled and configured
    if (formValue.enableGrouping && this.groupByConfig) {
      payload.groupBy = {
        fields: this.groupByConfig.fields || [],
        aggregations: (this.groupByConfig.aggregations || []).map(agg => ({
          field: agg.field,
          function: agg.function,
          alias: agg.alias
        }))
      };
    }
    
    // Add sorting if configured
    if (this.sortConfig && this.sortConfig.length > 0) {
      payload.orderBy = this.sortConfig.map(sort => ({
        field: sort.field,
        direction: sort.direction
      }));
    }
    
    // Add chart config only if enabled and valid
    if (formValue.enableCharting && this.chartConfig && this.chartConfig.enabled) {
      // Validate chart fields exist in selected fields
      const availableFields = [...this.selectedFields.map(f => f.name)];
      if (this.calculatedFields) {
        availableFields.push(...this.calculatedFields.map(c => c.name));
      }
      
      const validXAxis = availableFields.includes(this.chartConfig.xAxis);
      const validYAxis = this.chartConfig.yAxis?.every(field => availableFields.includes(field));
      
      if (validXAxis && validYAxis) {
        payload.chartConfig = {
          enabled: true,
          type: this.chartConfig.type,
          title: this.chartConfig.title,
          xAxis: this.chartConfig.xAxis,
          yAxis: this.chartConfig.yAxis
        };
      }
    }
    
    return payload;
  }

  async deleteReport(report: CustomReport) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the report "${report.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.customReportService.deleteReport(report._id!);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Report deleted successfully',
            life: 3000
          });
          this.loadReports();
        } catch (error: any) {
          this.messageService.add({
            severity: 'error',
            summary: 'Delete Error',
            detail: error.message || 'Failed to delete report'
          });
        }
      }
    });
  }

  // UI helper methods
  getFieldTypeIcon(type: string): string {
    switch (type) {
      case 'string': return 'pi pi-align-left';
      case 'number': return 'pi pi-hashtag';
      case 'date': return 'pi pi-calendar';
      case 'boolean': return 'pi pi-check-square';
      case 'array': return 'pi pi-list';
      default: return 'pi pi-question-circle';
    }
  }

  getReportStatusIcon(report: CustomReport): string {
    if (!report.lastExecuted) return 'pi pi-clock';
    return 'pi pi-check-circle';
  }

  getReportStatusColor(report: CustomReport): string {
    if (!report.lastExecuted) return 'warning';
    return 'success';
  }

  formatDate(date: Date | string): string {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  }

  formatExecutionTime(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  closeDialogs() {
    this.reportBuilderVisible = false;
    this.reportPreviewVisible = false;
    this.reportExecutionVisible = false;
    this.currentReport = null;
  }

  // Helper methods for template
  getCategoryOptions() {
    return [
      { label: 'All Categories', value: '' },
      ...this.categories.map(c => ({ label: c, value: c }))
    ];
  }

  trackByFieldName(index: number, field: any): string {
    return field.name;
  }

  getFieldLabel(fieldName: string): string {
    return this.availableFields.find(f => f.name === fieldName)?.label || fieldName;
  }

  // Format date to European standard (DD/MM/YYYY HH:MM:SS)
  formatDateEuropean(date: Date | string): string {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return date.toString();
    
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  // Format value for display with proper date formatting
  formatValueForDisplay(value: any, fieldName?: string): string {
    if (value === null || value === undefined) return '';
    if (!fieldName) return value.toString();
    
    // Check if this is a date field or looks like a date
    const isDateField = fieldName.toLowerCase().includes('date') || 
                       fieldName.toLowerCase().includes('created') ||
                       fieldName.toLowerCase().includes('updated') ||
                       fieldName.toLowerCase().includes('time');
    
    if (isDateField || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
      return this.formatDateEuropean(value);
    }
    
    // Format numbers with proper separators
    if (typeof value === 'number') {
      if (fieldName.toLowerCase().includes('value') || fieldName.toLowerCase().includes('amount')) {
        return new Intl.NumberFormat('de-DE', { 
          style: 'currency', 
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(value);
      }
      return new Intl.NumberFormat('de-DE').format(value);
    }
    
    return value.toString();
  }

  // Get all available columns from the current data
  getDataColumns(): string[] {
    if (!this.executionResult?.data || !Array.isArray(this.executionResult.data) || this.executionResult.data.length === 0) {
      return [];
    }
    
    try {
      return Object.keys(this.executionResult.data[0] || {});
    } catch (error) {
      console.error('Error getting data columns:', error);
      return [];
    }
  }

  // Check if we should use dynamic columns (when selectedFields don't match data)
  shouldUseDynamicColumns(): boolean {
    if (!this.executionResult?.data || this.executionResult.data.length === 0) {
      return false;
    }
    
    const dataKeys = Object.keys(this.executionResult.data[0]);
    const selectedFields = this.currentReport?.selectedFields || [];
    
    if (selectedFields.length === 0) {
      return true; // No selected fields, use all data columns
    }
    
    // Check if any selected fields exist in the data
    const hasMatchingFields = selectedFields.some(field => dataKeys.includes(field));
    return !hasMatchingFields;
  }

  // Preview-specific helper methods
  getPreviewDataColumns(): string[] {
    if (!this.previewData || !Array.isArray(this.previewData) || this.previewData.length === 0) {
      return [];
    }
    
    try {
      return Object.keys(this.previewData[0] || {});
    } catch (error) {
      console.error('Error getting preview data columns:', error);
      return [];
    }
  }

  hasMatchingPreviewFields(): boolean {
    if (!this.previewData || !Array.isArray(this.previewData) || this.previewData.length === 0) {
      return false;
    }
    
    if (!this.selectedFields || !Array.isArray(this.selectedFields) || this.selectedFields.length === 0) {
      return false;
    }
    
    try {
      const dataKeys = Object.keys(this.previewData[0] || {});
      const selectedFieldNames = this.selectedFields.map(f => f?.name).filter(Boolean);
      
      // Check if any selected fields exist in the preview data
      return selectedFieldNames.some(field => dataKeys.includes(field));
    } catch (error) {
      console.error('Error checking preview field matching:', error);
      return false;
    }
  }

  getPreviewColumnCount(): number {
    if (this.previewData && this.previewData.length > 0 && (this.selectedFields.length === 0 || !this.hasMatchingPreviewFields())) {
      return this.getPreviewDataColumns().length;
    }
    return (this.selectedFields?.length || 0) + (this.calculatedFields?.length || 0);
  }

  // Safe method to get table colspan
  getTableColspan(): number {
    if (this.shouldUseDynamicColumns()) {
      return this.getDataColumns().length || 1;
    }
    
    const selectedFieldsCount = this.currentReport?.selectedFields?.length || 0;
    const calculatedFieldsCount = this.currentReport?.calculatedFields?.length || 0;
    const aggregationsCount = this.currentReport?.groupBy?.aggregations?.length || 0;
    
    return Math.max(selectedFieldsCount + calculatedFieldsCount + aggregationsCount, 1);
  }

  // Helper methods for template conditions
  shouldShowDynamicPreviewColumns(): boolean {
    return (this.previewData && this.previewData.length > 0) && 
           ((this.selectedFields && this.selectedFields.length === 0) || !this.hasMatchingPreviewFields());
  }

  shouldShowOriginalPreviewColumns(): boolean {
    return (!this.previewData || this.previewData.length === 0) || 
           ((this.selectedFields && this.selectedFields.length > 0) && this.hasMatchingPreviewFields());
  }

  // Enhanced helper methods
  getNumericFields(): ReportField[] {
    return this.selectedFields.filter(f => f.type === 'number');
  }

  getGroupableFields(): ReportField[] {
    return this.selectedFields.filter(f => f.type !== 'array');
  }

  getChartableFields(): ReportField[] {
    return this.selectedFields.filter(f => f.type === 'number' || f.type === 'date');
  }

  getAllFields(): ReportField[] {
    const allFields = [...this.selectedFields];
    
    // Add calculated fields as selectable fields
    this.calculatedFields.forEach(calc => {
      allFields.push({
        name: calc.name,
        label: calc.label,
        type: calc.returnType,
        source: 'calculated',
        path: calc.name,
        category: 'Calculated',
        description: calc.description,
        accessLevel: ['all'],
        isCalculated: true,
        formula: calc.formula
      });
    });
    
    return allFields;
  }

  canCreateChart(): boolean {
    return this.getChartableFields().length > 0 && this.selectedFields.length > 1;
  }

  hasAggregations(): boolean {
    return !!(this.groupByConfig && this.groupByConfig.aggregations.length > 0);
  }

  toggleChartView() {
    this.chartViewVisible = !this.chartViewVisible;
  }

  previewChart() {
    if (this.chartConfig && this.executionResult) {
      this.generateChartData();
      this.chartViewVisible = true;
    }
  }

  getAggregatedFieldName(aggregation: AggregationConfig): string {
    return `${aggregation.function}(${this.getFieldLabel(aggregation.field)})`;
  }

  validateStep(step: number): boolean {
    switch (step) {
      case 0: // Fields
        return this.selectedFields.length > 0;
      case 1: // Calculations
        return true; // Optional step
      case 2: // Grouping
        return true; // Optional step
      case 3: // Filters
        return true; // Optional step
      case 4: // Charts
        return true; // Optional step
      case 5: // Settings
        return this.reportForm.valid;
      default:
        return true;
    }
  }

  canProceedToNextStep(): boolean {
    return this.validateStep(this.builderStep);
  }

  getStepValidationMessage(step: number): string {
    switch (step) {
      case 0:
        return this.selectedFields.length === 0 ? 'Please select at least one field' : '';
      case 5:
        return this.reportForm.invalid ? 'Please fill in all required settings' : '';
      default:
        return '';
    }
  }

  // Helper methods for template expressions
  getAvailableFieldNames(): string {
    return this.getAvailableFieldsForFormula().map(f => f.name).join(', ');
  }

  onChartingToggle(enabled: boolean): void {
    this.reportForm.controls['enableCharting'].setValue(enabled);
    if (enabled) {
      this.initializeChart();
    }
  }

  getChartTypeLabel(): string {
    if (!this.chartConfig?.type) return '';
    return this.chartTypeOptions.find(t => t.value === this.chartConfig?.type)?.label || '';
  }

  getChartXAxisLabel(): string {
    if (!this.chartConfig?.xAxis) return '';
    return this.getFieldLabel(this.chartConfig.xAxis);
  }

  getChartYAxisLabels(): string {
    if (!this.chartConfig?.yAxis || this.chartConfig.yAxis.length === 0) return '';
    return this.chartConfig.yAxis.map(f => this.getFieldLabel(f)).join(', ');
  }

  hasValidChartConfig(): boolean {
    return !!(this.chartConfig?.xAxis && this.chartConfig?.yAxis && this.chartConfig.yAxis.length > 0);
  }

  // Debug and testing methods
  createSimplifiedPayload(): any {
    return {
      name: this.reportForm.get('name')?.value || 'Test Report',
      description: this.reportForm.get('description')?.value || 'Test Description',
      isPublic: false,
      limit: 10,
      exportFormats: ['csv'],
      selectedFields: this.selectedFields.slice(0, 3).map(f => f.name), // Only first 3 fields
      conditions: []
    };
  }

  async testSimplifiedPreview() {
    try {
      this.loading = true;
      const simplifiedPayload = this.createSimplifiedPayload();
      
      console.log('Testing Simplified Payload:', JSON.stringify(simplifiedPayload, null, 2));
      
      this.previewData = await this.customReportService.previewReport(simplifiedPayload);
      this.reportPreviewVisible = true;
      
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Simplified preview worked! You can now add more complexity.',
        life: 3000
      });
    } catch (error: any) {
      console.error('Even simplified payload failed:', error);
      
      this.messageService.add({
        severity: 'error',
        summary: 'Simplified Test Failed',
        detail: 'The backend has an issue with basic field selection. Check field names or backend configuration.'
      });
    } finally {
      this.loading = false;
    }
  }

  validateFieldNames(fields: string[]): string[] {
    // Basic field validation - ensure fields exist in our definitions
    const validFields = this.availableFields.map(f => f.name);
    return fields.filter(field => validFields.includes(field));
  }

  fixChartConfiguration() {
    if (this.chartConfig && this.chartConfig.enabled) {
      const availableFields = [...this.selectedFields.map(f => f.name)];
      
      // Add calculated field names to available fields
      if (this.calculatedFields && this.calculatedFields.length > 0) {
        availableFields.push(...this.calculatedFields.map(c => c.name));
      }
      
      // Fix X-axis if it's not in available fields
      if (!availableFields.includes(this.chartConfig.xAxis)) {
        this.chartConfig.xAxis = availableFields[0] || 'user_first_name';
      }
      
      // Fix Y-axis fields
      if (this.chartConfig.yAxis) {
        this.chartConfig.yAxis = this.chartConfig.yAxis.filter(field => 
          availableFields.includes(field)
        );
        
        // If no valid Y-axis fields, add some defaults
        if (this.chartConfig.yAxis.length === 0) {
          const numericFields = availableFields.filter(field => 
            field.includes('value') || field.includes('count') || field.includes('amount')
          );
          this.chartConfig.yAxis = numericFields.slice(0, 2);
        }
      }
    }
  }
}
